import React, { useState, useEffect } from 'react'
import AssessmentIcon from '@mui/icons-material/Assessment'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import Collapse from '@mui/material/Collapse'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import FolderIcon from '@mui/icons-material/Folder'
import IconButton from '@mui/material/IconButton'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import Paper from '@mui/material/Paper'
import PublishIcon from '@mui/icons-material/Publish'
import SaveAltIcon from '@mui/icons-material/SaveAlt'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import UploadPresentation from '../overlays/UploadPresentation'
import ContextMenu from '../ui/ContextMenu'
import { dateParser, dynamicSort, HandlePresentationFilter } from '../utilities/helperFunctions'
import excelbw from '../../images/svg/excel_bw.svg'
import pptbw from '../../images/svg/ppt_bw.svg'
import downArrow from '../../images/js/downArrow'
import upArrow from '../../images/js/upArrow'
import check from '../../images/js/check'
import x from '../../images/js/x'

// ─── Constants ────────────────────────────────────────────────────────────────
const ENTRIES_PER_PAGE = 5
const COL_WIDTHS = ['65%', '25%', '15%', '10%']

function PresentationProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`
    }
}

// Shared colgroup keeps column widths identical across every separate <Table>
function TableColGroup() {
    return (
        <colgroup>
            {COL_WIDTHS.map((w, i) => (
                <col key={i} style={{ width: w }} />
            ))}
        </colgroup>
    )
}

// ─── Sub-component: Folder node ───────────────────────────────────────────────
function FolderNode({ folder, presentationCount, renderPresentations }) {
    const [open, setOpen] = useState(false)
    const count = presentationCount(folder.id)

    return (
        <Box sx={{ mb: 0.5 }}>
            <Box
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, borderRadius: 1,
                    cursor: 'pointer', bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' }
                }}
                onClick={() => setOpen(o => !o)}
            >
                {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                <FolderIcon fontSize="small" color="primary" />
                <Typography component="span" style={{ fontWeight: 700 }}>{folder.folderName} ({count})</Typography>
            </Box>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <Box sx={{ mt: 0.5 }}>
                    <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                        <TableColGroup />
                        <TableBody>
                            {renderPresentations(folder.id, true)}
                        </TableBody>
                    </Table>
                </Box>
            </Collapse>
        </Box>
    )
}
// ─── Main Component ───────────────────────────────────────────────────────────
function Presentations(props) {
    const [xPos, setXPos] = useState(null)
    const [yPos, setYPos] = useState(null)
    const [rightClick, setRightClick] = useState(false)
    const [ctxId, setCtxId] = useState(null)
    const [rename, setRename] = useState(false)
    const [renameField, setRenameField] = useState('')
    const [renameTarget, setRenameTarget] = useState(null)
    const [value, setValue] = useState(0)

    useEffect(() => {
        const filter = HandlePresentationFilter(props.presentationFilter)
        setValue(filter['value'])
        handleFilters(filter['label'])
    }, [])

    const handleChangeForm = (event, newValue) => {
        setValue(newValue)
        handleFilters(newValue === 0 ? 'myPresentations' : newValue === 1 ? 'allPresentations' : 'templates')
    }

    const changeRename = (event) => {
        event.preventDefault()
        setRenameField(event.target.value)
    }

    const downloadPres = (presId) => {
        const pres = props.presentationList.find(p => p.id === parseInt(presId))
        const ext = pres.documentTypeID === 0 ? '.pptx' : pres.documentTypeID === 1 ? '.xlsx' : ''
        props.downloadPresentation(presId, pres.name + ext)
    }

    const contextDownloadPres = (presId) => {
        const pres = props.presentationList.find(p => p.id === parseInt(presId))
        const ext = pres.documentTypeID === 0 ? '.pptx' : pres.documentTypeID === 1 ? '.xlsx' : ''
        props.downloadPresentation(presId, pres.name + ext)
    }

    const contextDeletePresentation = (presId) => {
        props.showPopup('deletePresentation', presId)
    }

    const handleSort = (event) => {
        event.preventDefault()
        const split = props.presentationFilter.split('|')[1]
        const category = event.currentTarget.dataset.category
        const string = props.presentationFilter === `sort|${split}|${category}|asc`
            ? `sort|${split}|${category}|desc`
            : `sort|${split}|${category}|asc`
        props.applyFilter(string, 'presentations')
    }

    const renderArrow = (string) => {
        if (!props.presentationFilter.includes(string)) return null
        return props.presentationFilter.includes('asc')
            ? downArrow('black', 18, null, string)
            : upArrow('black', 18, null, string)
    }

    const goToPresentation = (presId) => {
        const documentTypeID = props.presentationList.find(p => p.id === parseInt(presId)).documentTypeID
        if (documentTypeID === 0) window.location = `/presentations/powerpoint/${presId}`
        else if (documentTypeID === 1) window.location = `/presentations/excel/${presId}`
    }

    const handleEditConnection = (presId) => {
        const pres = props.presentationList.find(p => p.id === presId)
        props.getConnectionInfos(presId, pres.documentTypeID)
        props.showPopup('editPresentationInfo', presId)
    }

    const isThisPresentationChildOfStory = (presInfo, allPres) => {
        if (presInfo.parentPresentationId === null) return false
        if (presInfo.parentPresentationId === presInfo.id) return false
        const parent = allPres.find(p => p.id === presInfo.parentPresentationId)
        if (!parent) return false
        return parent.isStory
    }

    const handleFilters = (name) => {
        props.applyFilter('filter|' + name, 'presentations')
    }

    const handlePresentation = (presId, name) => {
        if (name !== 'delete' && name !== 'download' && name !== 'edit') {
            goToPresentation(presId)
        }
    }

    const handleContextMenu = (event, presId) => {
        event.preventDefault()
        if (rightClick) {
            setXPos(null); setYPos(null); setCtxId(null); setRightClick(false)
        } else {
            setXPos(event.pageX - 70); setYPos(event.pageY - 70); setCtxId(presId); setRightClick(true)
        }
    }

    const handleUploadPres = (event) => {
        event.preventDefault()
        props.showPopup('uploadPresentation')
    }

    const handleUpdatePres = (event, presId) => {
        event.preventDefault()
        props.showPopup('updatePresentation', presId)
    }

    const handleDeletePres = (presId) => {
        props.showPopup('deletePresentation', presId)
    }

    const handleProgressBar = (presId) => {
        return Object.keys(props.presentationsProgress).includes(presId.toString())
            ? <div className="miniProgressBar"><div className="filler" style={{ width: `${props.presentationsProgress[presId.toString()] * 10 + 10}%` }} /></div>
            : null
    }

    const handleRename = (name, presId, indent = false) => {
        if (!rename || renameTarget !== presId) {
            return (
                <TableCell
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handlePresentation(presId, '')}
                    onContextMenu={(e) => handleContextMenu(e, presId)}
                    data-id={presId}
                >
                    <Box component="span" sx={{ ml: indent ? '50px' : 0 }}>
                        {name} {handleProgressBar(presId)}
                    </Box>
                </TableCell>
            )
        }
        return (
            <TableCell>
                <Box sx={{ ml: indent ? '50px' : 0 }}>
                    <input type="text" data-name={name} onChange={changeRename} value={renameField === '' ? name : renameField} onKeyUp={handleRenameSubmit} style={{ width: '95%' }} />
                </Box>
            </TableCell>
        )
    }

    const handleRenameSubmit = (event) => {
        event.preventDefault()
        if (event.keyCode === 13) {
            props.sendRenamePresentation(renameTarget, renameField === '' ? event.target.dataset.name : renameField)
        }
    }

    const getFilteredPresentationList = (list) => {
        if (props.presentationFilter.includes('filter|')) {
            const split = props.presentationFilter.split('|')
            if (split[1] === 'myPresentations') {
                return list.filter(p => ((p.modifiedBy !== null) ? p.modifiedBy.toLowerCase() : p.createdBy.toLowerCase()) === props.email.toLowerCase()).sort(dynamicSort('modifiedDate')).reverse()
            } else if (split[1] === 'templates') {
                return list.filter(p => p.isTemplate === true).sort(dynamicSort('modifiedDate')).reverse()
            }
            return list.sort(dynamicSort('modifiedDate')).reverse()
        }
        return list.filter(p => p.name.toLowerCase().includes(props.presentationFilter.toLowerCase())).sort(dynamicSort('modifiedDate')).reverse()
    }

    const getSortedPresentationList = (list) => {
        const splitFilter = props.presentationFilter.split('|')
        let filterCategory = splitFilter[1]
        let sortCategory = splitFilter[2] === 'undefined' ? 'modifiedDate' : splitFilter[2]
        let direction = splitFilter[3] === 'undefined' ? 'asc' : splitFilter[3]

        let filtered = list
        if (filterCategory === 'myPresentations') {
            filtered = list.filter(p => ((p.modifiedBy !== null) ? p.modifiedBy.toLowerCase() : p.createdBy.toLowerCase()) === props.email.toLowerCase()).sort(dynamicSort('modifiedDate')).reverse()
        } else if (filterCategory === 'templates') {
            filtered = list.filter(p => p.isTemplate === true).sort(dynamicSort('modifiedDate')).reverse()
        } else {
            filtered = list.sort(dynamicSort('modifiedDate')).reverse()
        }

        return direction === 'asc' ? filtered.sort(dynamicSort(sortCategory)) : filtered.sort(dynamicSort(sortCategory)).reverse()
    }

    const getTagFilteredPresentationList = (list) => {
        const filtered = []
        const tagsFilter = props.presentationTagsFilter
        for (var i in list) {
            const pres = list[i]
            for (var j in pres.presentationTags) {
                const tag = pres.presentationTags[j]
                for (var k in tagsFilter) {
                    if (tag.tagID == tagsFilter[k].id && !filtered.includes(pres)) {
                        filtered.push(pres)
                        break
                    }
                }
            }
        }
        return filtered
    }

    const renderPresentations = (folderId, indent = false) => {
        if (props.presentationList.Count === 0) return null
        let list = props.presentationList.filter(f => f.folderID == folderId)
        if (list.length === 0) return null

        list = props.presentationFilter.includes('sort|') ? getSortedPresentationList(list) : getFilteredPresentationList(list)

        if (props.presentationTagsFilter && props.presentationTagsFilter.length > 0) {
            list = getTagFilteredPresentationList(list)
        }

        const isIndented = folderId !== null

        return list.map(pres => (
            <TableRow key={pres.id}>
                {handleRename(pres.name, pres.id, isIndented)}
                <TableCell align="right" onClick={() => handlePresentation(pres.id, '')} onContextMenu={(e) => handleContextMenu(e, pres.id)} data-id={pres.id} style={{ cursor: 'pointer' }}>
                    {pres.createdBy}
                </TableCell>
                <TableCell align="right" onClick={() => handlePresentation(pres.id, '')} onContextMenu={(e) => handleContextMenu(e, pres.id)} data-id={pres.id} style={{ cursor: 'pointer' }}>
                    {dateParser(pres.modifiedDate)}
                </TableCell>
                <TableCell align="right">
                    <Tooltip title={pres.documentTypeID === 0 ? 'Powerpoint Document' : 'Excel Document'}>
                        <IconButton size="small" onClick={() => handlePresentation(pres.id, '')}>
                            <img src={pres.documentTypeID === 0 ? pptbw : excelbw} alt={pres.documentTypeID === 0 ? 'Powerpoint Document' : 'Excel Document'} className="excelIcons" style={{ width: '17px' }} />
                        </IconButton>
                    </Tooltip>
                    {isThisPresentationChildOfStory(pres, list) && (
                        <Tooltip title="This Presentation Is A Story">
                            <IconButton size="small" onClick={() => handlePresentation(pres.id, '')}>
                                <MenuBookIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {props.role !== 'Viewer' && (
                        <Tooltip title="Edit Presentation">
                            <IconButton size="small" onClick={() => handleEditConnection(pres.id)} data-id={pres.id} id="editpresentation_btn">
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {props.role !== 'Viewer' && pres.isStory === true && (
                        <Tooltip title="Update Parent Story Presentation">
                            <IconButton size="small" onClick={(e) => handleUpdatePres(e, pres.id)} data-id={pres.id} id="updatepresentation_btn">
                                <PublishIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Download Presentation">
                        <IconButton size="small" onClick={() => downloadPres(pres.id)} data-id={pres.id} id="downloadpresentation_btn">
                            <SaveAltIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Presentation">
                        <IconButton size="small" onClick={() => handleDeletePres(pres.id)} data-id={pres.id} id="deletepresentation_btn">
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>
        ))
    }

    const presentationCount = (folderId) => {
        if (props.presentationList.Count === 0) return 0
        let list = props.presentationList.filter(f => f.folderID == folderId)
        if (list.length === 0) return 0
        list = getFilteredPresentationList(list)
        if (props.presentationTagsFilter && props.presentationTagsFilter.length > 0) {
            list = getTagFilteredPresentationList(list)
        }
        return list.length
    }

    const renamePresentation = (presId) => {
        setXPos(null); setYPos(null); setCtxId(null); setRightClick(false)
        setRename(true); setRenameTarget(parseFloat(presId))
    }

    return (
        <>
            <div className="mainWrapper">
                <div className="bodyPart">
                    <div className="contentWrapper">
                        <h2 className="pageHead">
                            <AssessmentIcon />
                            <span className="mainTitleBox">Presentation</span>
                            <div className="rightButton">
                                {props.role !== 'Viewer' && (
                                    <button className="btn" onClick={handleUploadPres} id="upload_btn">Upload New</button>
                                )}
                            </div>
                        </h2>
                        <div className="tabOutMain presentAtionOut">
                            <AppBar position="static">
                                <div className="TabsData">
                                    <Tabs value={value} onChange={handleChangeForm}>
                                        <Tab label="MY PRESENTATIONS" {...PresentationProps(0)} />
                                        <Tab label="ALL PRESENTATIONS" {...PresentationProps(1)} />
                                        <Tab label="Templates" {...PresentationProps(2)} />
                                    </Tabs>
                                </div>
                            </AppBar>
                            <Paper elevation={3} className="dbFunctionsPaper">
                                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                                    <TableColGroup />
                                    <TableHead>
                                        <TableRow>
                                            <TableCell data-category="name" onClick={handleSort} style={{ cursor: 'pointer' }}>
                                                Presentation Name {renderArrow('name')}
                                            </TableCell>
                                            <TableCell align="right" data-category="createdBy" onClick={handleSort} style={{ cursor: 'pointer' }}>
                                                Author {renderArrow('createdBy')}
                                            </TableCell>
                                            <TableCell align="right" data-category="modifiedDate" onClick={handleSort} style={{ cursor: 'pointer' }}>
                                                Last Edited {renderArrow('modifiedDate')}
                                            </TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                </Table>

                                {props.custFolderList.map(folder => (
                                    <FolderNode
                                        key={`folder-${folder.id}`}
                                        folder={folder}
                                        presentationCount={presentationCount}
                                        renderPresentations={renderPresentations}
                                    />
                                ))}

                                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                                    <TableColGroup />
                                    <TableHead>
                                        <TableRow style={{ height: '1px' }}>
                                            <TableCell /><TableCell /><TableCell /><TableCell />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {renderPresentations(null)}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </div>
                    </div>
                </div>
            </div>
            {rightClick && (
                <ContextMenu
                    id={ctxId}
                    xPos={xPos}
                    yPos={yPos}
                    menuTemplate={['go to presentation', 'rename presentation', 'download presentation', 'delete presentation']}
                    goToPresentation={goToPresentation}
                    deletePresentation={contextDeletePresentation}
                    renamePresentation={renamePresentation}
                    downloadPresentation={contextDownloadPres}
                />
            )}
        </>
    )
}

export default Presentations